import { ApolloLink } from '@apollo/client';
import { Observable, Observer, Subscription } from 'rxjs';
import { InternalError, Tools } from '../shared/Tools';
import { ApolloContextKey } from '../enums/ApolloContextKey';
export interface OperationQueueEntry {
  operation: ApolloLink.Operation;
  forward: ApolloLink.ForwardFunction;
  observer: Observer<ApolloLink.Result>;
  subscription?: { unsubscribe: () => void };
}

function extractKeys(operation: ApolloLink.Operation): string[] {
  return operation.getContext().serializationKey;
}

/**
 * Serialize queries with the same context.serializationKey, meaning that
 * all previous queries must complete for the next query with the same
 * context.serializationKey to be started.
 *
 * Actions are typically made in this order:
 *   - Enqueue all queries in an array per key
 *   - Once the downstream operation completes, "observer.complete()" is called,
 *     and the Observable cleanup function is run, removing the query
 *     from the array, and running the first (next) query in that array
 *
 */
export default class CustomSerializingLink extends ApolloLink {
  private readonly opQueues: { [key: string]: OperationQueueEntry[] } = {};

  public request(
    operation: ApolloLink.Operation,
    forward: ApolloLink.ForwardFunction,
  ) {
    const keys = extractKeys(operation);
    if (!keys?.length) {
      return forward(operation);
    }

    return new Observable<ApolloLink.Result>((observer) => {
      const entry = { operation, forward, observer };
      this.enqueue(keys, entry);

      // Cleanup function
      return () => {
        this.cleanUp(keys, entry);
        this.nextOp(keys);
      };
    });
  }

  /**
   * Add an operation to the end of the queue. If it is the first operation in the queue, start it.
   */
  private readonly enqueue = (keys: string[], entry: OperationQueueEntry) => {
    for (const key of keys) {
      if (!this.opQueues[key]) {
        this.opQueues[key] = [];
      }
      this.opQueues[key].push(entry);
    }
    if (keys.every((key) => this.opQueues[key].length === 1)) {
      this.startFirstOpIfNotStarted(keys[0]);
    }
  };

  /**
   * Cancel the operation by removing it from the queue and unsubscribing if it is currently in progress.
   */
  private readonly cleanUp = (
    keys: string[],
    entryToRemove: OperationQueueEntry,
  ) => {
    for (const key of keys) {
      if (!this.opQueues[key]) {
        /* should never happen */ continue;
      }
      const idx = this.opQueues[key].indexOf(entryToRemove);

      if (idx >= 0) {
        this.opQueues[key].splice(idx, 1);
      }
    }
    entryToRemove.subscription?.unsubscribe();
  };

  /**
   * From the stacks of the current mutation keys, take the next mutation and execute it if ready
   */
  private readonly nextOp = (keys: string[]) => {
    const nextMutations = keys
      .map((key) => this.opQueues[key]?.[0]?.operation)
      .filter((v): v is ApolloLink.Operation => Boolean(v));

    const nextMutationsUnique = [...new Set(nextMutations)];
    nextMutationsUnique.forEach((nextMutationUnique) => {
      if (this.isOperationReady(nextMutationUnique)) {
        const mutationKeys = extractKeys(nextMutationUnique);
        if (mutationKeys?.[0]) this.startFirstOpIfNotStarted(mutationKeys[0]);
      }
    });
  };

  /**
   * Start the first operation in the queue if it hasn't been started yet
   */
  private readonly startFirstOpIfNotStarted = (key: string) => {
    // At this point, the queue always exists, but it may not have any elements
    // If it has no elements, we free up the memory it was using.
    if (this.opQueues[key].length === 0) {
      delete this.opQueues[key];
      return;
    }
    const op = this.opQueues[key][0];
    const { operation, forward, observer, subscription } = op;
    if (subscription) {
      return;
    }

    // Assign before subscribing: a downstream link can complete synchronously.
    const downstream = new Subscription();
    op.subscription = downstream;
    downstream.add(
      forward(operation).subscribe({
        next: (v: ApolloLink.Result) => {
          observer.next?.(v);
        },
        error: (e: unknown) => {
          this.killAll(op, e);
        },
        complete: () => {
          observer.complete?.();
        },
      }),
    );
  };

  /**
   * Cancela la mutación dada y las posteriores a esta, teniendo en cuenta el tipo de error
   */
  private readonly killAll = (entry: OperationQueueEntry, error: unknown) => {
    const keysIndex = this.getKeysIndex(entry);
    const cancelled = new Set<OperationQueueEntry>();
    for (const key in keysIndex) {
      for (const mutation of (this.opQueues[key] ?? []).slice(keysIndex[key])) {
        cancelled.add(mutation);
      }
    }
    // Remove all dependent writes before error notifications trigger teardown
    // and start other queued operations.
    for (const key in this.opQueues) {
      this.opQueues[key] = this.opQueues[key].filter(
        (item) => !cancelled.has(item),
      );
    }
    for (const mutation of cancelled) {
      mutation.subscription?.unsubscribe();
      mutation.observer.error?.(
        mutation !== entry && Tools.isGraphqlError(error)
          ? new InternalError(
              `Falló la consulta relacionada ${entry.operation.getContext()[ApolloContextKey.CUSTOM_OPERATION_NAME]}`,
            )
          : error,
      );
    }
    this.nextOp(Object.keys(keysIndex));
  };

  /**
   * Devuelve los indices en cada key a partir de los cuales se deben cancelar las mutaciones
   */
  private readonly getKeysIndex = (
    entry: OperationQueueEntry,
    keyIndexes: Record<string, number> = {},
  ) => {
    // Por cada key de la mutación
    const { operation } = entry;
    const keys = extractKeys(operation);
    for (const key of keys) {
      const keyMutations = this.opQueues[key] ?? [];
      const keyMutationIndex = keyMutations.indexOf(entry);

      const keyCurrentIndex = keyIndexes[key];

      if (keyCurrentIndex === undefined || keyCurrentIndex > keyMutationIndex) {
        keyIndexes[key] = keyMutationIndex;
      }

      const nextMutation: OperationQueueEntry | undefined =
        keyMutations[keyMutationIndex + 1];

      if (nextMutation) this.getKeysIndex(nextMutation, keyIndexes);
    }

    return keyIndexes;
  };

  private readonly isOperationReady = (operation: ApolloLink.Operation) => {
    const keys = extractKeys(operation);
    return keys.every(
      (key) => this.opQueues[key]?.[0]?.operation === operation,
    );
  };
}
